import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  ApplicationConfig,
  LOCALE_ID,
  provideZoneChangeDetection,
} from '@angular/core';
import {
  provideRouter,
  withComponentInputBinding,
  withHashLocation,
} from '@angular/router';
import {
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalInterceptorConfiguration,
  MsalService,
} from '@azure/msal-angular';
import {
  InteractionType,
  IPublicClientApplication,
  PublicClientApplication,
} from '@azure/msal-browser';
import {
  ConfirmationService,
  MessageService,
  OverlayService,
} from 'primeng/api';
import { providePrimeNG } from 'primeng/config';

import { environment } from '../environments/environment';
import { AuthInterceptor } from '../shared/interceptors/auth.interceptor';
import { routes } from './app.routes';

const _apiUrl = environment.apiUrl;
const _clientIdAzure = environment.azure.clientId;
const _authorityAzure = `https://login.microsoftonline.com/${environment.azure.tenantId}`;
const _redirectAplicationAzure = environment.azure.redirectUri;
const _scopesAzure = environment.azure.scopes;

/**
 * Crea la instancia de PublicClientApplication para MSAL.
 *
 * Es la **única** instancia de la aplicación: `AuthService` la consume a través
 * de `MsalService` en lugar de crear la suya. Antes había dos, con el mismo
 * `clientId` y la misma caché en `localStorage` pero configuraciones distintas,
 * y competían por claves compartidas como `msal.interaction.status` (cuyo
 * nombre no incluye el `clientId`). Esa carrera dejaba estados a medias que
 * acababan borrando el hash de la URL, y con `withHashLocation()` perder el
 * hash equivale a expulsar al usuario de la pantalla en que estaba. Ver
 * `specs/spec-036-aislamiento-visor-publico.md`.
 */
export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: _clientIdAzure,
      authority: _authorityAzure,
      redirectUri: _redirectAplicationAzure,
      navigateToLoginRequestUrl: true,
    },
    cache: {
      cacheLocation: 'localStorage',
      storeAuthStateInCookie: true,
      secureCookies: true,
    },
    system: {
      allowRedirectInIframe: true,
      windowHashTimeout: 60000,
      iframeHashTimeout: 6000,
    },
  });
}

/**
 * Configura el interceptor para MSAL.
 */
export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set(_apiUrl, _scopesAzure);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
    authRequest: {
      extraScopesToConsent: _scopesAzure,
    },
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withHashLocation(), withComponentInputBinding()),
    provideHttpClient(withInterceptorsFromDi()),
    { provide: LOCALE_ID, useValue: 'es-MX' },
    providePrimeNG({
      translation: {
        // Mensajes para multiSelect
        emptyMessage: 'No hay datos disponibles',
        emptyFilterMessage: 'No se encontraron resultados',
        selectionMessage: '{0} elementos seleccionados',
        // Mensajes para autoComplete
        emptySearchMessage: 'No se encontraron resultados',
      },
    }),
    MsalService,
    MsalBroadcastService,
    /**
     * Se provee la instancia de MSAL creada por la MSALInstanceFactory
     */
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },

    /**
     * Se provee la configuración para el interceptor de MSAL
     */
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },

    /**
     * Provisión del interceptor MSAL a la aplicación.
     * 'multi: true' indica que pueden existir múltiples interceptores.
     */
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },

    /**
     * Providers para los servicios de mensajes y confirmación de PrimeNG
     * para poder usarlos en toda la aplicación.
     */
    MessageService,
    ConfirmationService,
    OverlayService,
  ],
};
